import { useCallback } from 'react'
import { getLanguageLocalStorage } from '~/features/i18n/utils/languageLocalStorage'
import { VideoSubtitle } from '~/types/api/videos'

const VIDEO_LANGUAGE_LOCAL_STORAGE_KEY = 'video_language'

export const useVideoLanguage = () => {
  const updatePlyrLanguage = useCallback((language: string) => {
    const plyrValue = JSON.parse(window.localStorage.getItem('plyr') || '{}')
    window.localStorage.setItem('plyr', JSON.stringify({ ...plyrValue, language, captions: true }))
  }, [])

  const disablePlyrCaptions = useCallback(() => {
    const plyrValue = JSON.parse(window.localStorage.getItem('plyr') || '{}')
    window.localStorage.setItem('plyr', JSON.stringify({ ...plyrValue, captions: false }))
  }, [])

  const getVideoLanguageOld = useCallback(
    (vtts: VideoSubtitle[]) => {
      const availableLanguages = vtts.map((vtt) => vtt.locale)
      const defaultLanguage = vtts.find((vtt) => vtt.default)?.locale ?? 'disabled'
      const value = window.localStorage.getItem(VIDEO_LANGUAGE_LOCAL_STORAGE_KEY)

      if (value === 'disabled') {
        disablePlyrCaptions()
        return value
      }

      if (value && availableLanguages.includes(value)) {
        updatePlyrLanguage(value)

        return value
      }

      const settingsLanguage = getLanguageLocalStorage()

      if (settingsLanguage && availableLanguages.includes(settingsLanguage)) {
        updatePlyrLanguage(settingsLanguage)
        window.localStorage.setItem(VIDEO_LANGUAGE_LOCAL_STORAGE_KEY, settingsLanguage)

        return settingsLanguage
      }

      if (defaultLanguage === 'disabled') {
        disablePlyrCaptions()
      } else {
        updatePlyrLanguage(defaultLanguage)
      }
      window.localStorage.setItem(VIDEO_LANGUAGE_LOCAL_STORAGE_KEY, defaultLanguage)

      return defaultLanguage
    },
    [updatePlyrLanguage, disablePlyrCaptions],
  )

  const getVideoLanguage = useCallback((vtts: VideoSubtitle[]) => {
    // 自動再生の場合、vttsが空になることがあるため、チェックを追加
    if (vtts.length === 0) {
      return 'disabled'
    }

    const availableLanguages = vtts.map((vtt) => vtt.locale)
    const defaultLanguage = vtts.find((vtt) => vtt.default)?.locale ?? 'disabled'
    const value = window.localStorage.getItem(VIDEO_LANGUAGE_LOCAL_STORAGE_KEY)

    if (value === 'disabled') {
      return value
    }

    if (value && availableLanguages.includes(value)) {
      return value
    }

    const settingsLanguage = getLanguageLocalStorage()

    if (settingsLanguage && availableLanguages.includes(settingsLanguage)) {
      window.localStorage.setItem(VIDEO_LANGUAGE_LOCAL_STORAGE_KEY, settingsLanguage)
      return settingsLanguage
    }

    window.localStorage.setItem(VIDEO_LANGUAGE_LOCAL_STORAGE_KEY, defaultLanguage)
    return defaultLanguage
  }, [])

  const updateVideoLanguage = useCallback((language: string) => {
    window.localStorage.setItem(VIDEO_LANGUAGE_LOCAL_STORAGE_KEY, language)
  }, [])

  return { getVideoLanguageOld, getVideoLanguage, updateVideoLanguage }
}
